import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
} from "@mui/material";
import { Send, Person, Description } from "@mui/icons-material";
import { User, Document, AssignDocumentsRequest } from "../types";
import { userAPI, documentAPI, docusignAPI } from "../services/api";

const DocumentAssignment: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    "Select Recipient",
    "Choose Documents",
    "Compose Message",
    "Review & Send",
  ];

  useEffect(() => {
    fetchUsers();
    fetchDocuments();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await userAPI.getAll();
      setUsers(response.data);
    } catch (err) {
      setError("Failed to fetch users");
      console.error("Error fetching users:", err);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await documentAPI.getActive();
      setDocuments(response.data);
    } catch (err) {
      setError("Failed to fetch documents");
      console.error("Error fetching documents:", err);
    }
  };

  const handleDocumentToggle = (documentId: string) => {
    setSelectedDocuments((prev) =>
      prev.includes(documentId)
        ? prev.filter((id) => id !== documentId)
        : [...prev, documentId]
    );
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (
      !selectedUser ||
      selectedDocuments.length === 0 ||
      !subject ||
      !message
    ) {
      setError("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const assignmentData: AssignDocumentsRequest = {
        userId: selectedUser,
        documentIds: selectedDocuments,
        subject,
        message,
      };

      const response = await docusignAPI.assignDocuments(assignmentData);

      setSuccess(
        `Documents assigned successfully! Envelope ID: ${response.data.envelopeId}`
      );

      // Reset form
      setSelectedUser("");
      setSelectedDocuments([]);
      setSubject("");
      setMessage("");
      setActiveStep(0);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to assign documents");
      console.error("Error assigning documents:", err);
    } finally {
      setLoading(false);
    }
  };

  const selectedUserData = users.find((user) => user._id === selectedUser);
  const selectedDocumentData = documents.filter((doc) =>
    selectedDocuments.includes(doc._id)
  );

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <FormControl fullWidth>
            <InputLabel>Select Recipient</InputLabel>
            <Select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              label="Select Recipient"
            >
              {users.map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  {user.name} ({user.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Documents to Assign
            </Typography>
            <List>
              {documents.map((document) => (
                <ListItem key={document._id} dense>
                  <ListItemIcon>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedDocuments.includes(document._id)}
                          onChange={() => handleDocumentToggle(document._id)}
                        />
                      }
                      label=""
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={document.title}
                    secondary={`For: ${document.document_for}`}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        );

      case 2:
        return (
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
              required
              placeholder="Please sign the following documents"
            />
            <TextField
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              fullWidth
              multiline
              rows={4}
              required
              placeholder="Hi, please review and sign the attached documents. Thank you!"
            />
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Assignment Details
            </Typography>

            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <Person sx={{ mr: 1, verticalAlign: "middle" }} />
                Recipient
              </Typography>
              {selectedUserData && (
                <Typography variant="body2" color="text.secondary">
                  {selectedUserData.name} ({selectedUserData.email})
                </Typography>
              )}
            </Paper>

            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <Description sx={{ mr: 1, verticalAlign: "middle" }} />
                Documents ({selectedDocumentData.length})
              </Typography>
              {selectedDocumentData.map((doc) => (
                <Typography
                  key={doc._id}
                  variant="body2"
                  color="text.secondary"
                >
                  • {doc.title}
                </Typography>
              ))}
            </Paper>

            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Message
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Subject:</strong> {subject}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Message:</strong> {message}
              </Typography>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  const isStepValid = (step: number) => {
    switch (step) {
      case 0:
        return selectedUser !== "";
      case 1:
        return selectedDocuments.length > 0;
      case 2:
        return subject !== "" && message !== "";
      case 3:
        return true;
      default:
        return false;
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" component="h1" gutterBottom>
        Document Assignment
      </Typography>

      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Assign documents to users for DocuSign e-signature
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ mb: 4 }}>{renderStepContent(activeStep)}</Box>

          <Box display="flex" justifyContent="space-between">
            <Box>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
              >
                Back
              </Button>
            </Box>
            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading || !isStepValid(activeStep)}
                  startIcon={<Send />}
                >
                  {loading ? "Sending..." : "Send for Signature"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!isStepValid(activeStep)}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DocumentAssignment;
